import React, { useCallback, useState } from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Divider,
    Stack,
    SxProps,
    Theme,
    Box,
    Popover,
    IconButton,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ColorScheme, theme } from '../../constants/theme';
import { IAdvertisement } from '../../types/global/media.types';
import CustomButton from '../Button/CustomButton';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { useDispatch } from 'react-redux';
import { clickAd } from '../../redux/adverts/action';
import { DialogueModal } from '../DialogueModal/DialogueModal';

interface IAdvertBanner {
    ad: IAdvertisement;
    sx?: SxProps<Theme>;
    themeStyle: ColorScheme;
    closeAd?: () => void;
    onAdClick?: (ad: IAdvertisement) => void;
}

const AdvertBanner: React.FC<IAdvertBanner> = ({ ad, sx, themeStyle, closeAd, onAdClick }) => {
    const { advertiser, feature_image, ad_title, call_to_action } = ad;
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleClickAd = useCallback(() => {
        dispatch(clickAd(ad._id));
    }, [ad._id, dispatch]);

    const handleMoreVertClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const handleWhyAmISeeingThis = () => {
        setIsModalOpen(true);
        handlePopoverClose();
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
    };
    return (
        <>
            <Card sx={{ width: 'inherit', backgroundColor: theme[themeStyle].secondaryBg, ...sx }}>
                <CardContent sx={{ p: 0 }}>
                    <CustomLazyLoadImage
                        src={feature_image}
                        alt={`${ad_title}_picture`}
                        style={{ width: '100%', height: 80, objectFit: 'cover' }}
                    />
                    <Stack mb={1} direction="row" justifyContent="space-between" spacing={1} alignItems="center" pl={2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CustomLazyLoadImage
                                src={advertiser.profile_picture}
                                alt={`${advertiser.name}_profile_image`}
                                style={{ width: '30px', height: '30px', borderRadius: '30px' }}
                            />

                            <Stack>
                                <Typography
                                    style={{
                                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                                        fontSize: 12,
                                        color: theme[themeStyle].tag_white,
                                        display: '-webkit-box',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 1, //numOfLines
                                    }}>
                                    {ad_title}
                                </Typography>
                                <Box style={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 8,
                                            color: theme[themeStyle].tag_white,
                                            fontWeight: '700',
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 1, //numOfLines
                                        }}>
                                        Sponsored -
                                    </Typography>{' '}
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                            fontSize: 8,
                                            color: theme[themeStyle].tag_white,
                                            fontWeight: 'bold',
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 1, //numOfLines
                                        }}>
                                        {' '}
                                        {advertiser.name}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                        <Stack sx={{ width: 'fit-content' }}>
                            <CardActions sx={{ width: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="center" width="100%" p={1}>
                                    <IconButton
                                        onClick={handleMoreVertClick}
                                        sx={{
                                            color: theme[themeStyle].tag_white,
                                            padding: '4px',
                                        }}>
                                        <MoreVertIcon />
                                    </IconButton>

                                    <CustomButton
                                        label={call_to_action ?? 'Learn More'}
                                        onClick={handleClickAd}
                                        customStyle={{
                                            backgroundColor: theme[themeStyle].main_blue,
                                        }}
                                        textStyle={{
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 1, //numOfLines
                                            color: theme[themeStyle].true_white,
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}
                                    />
                                </Stack>
                            </CardActions>
                        </Stack>
                    </Stack>
                </CardContent>
                <Divider />
            </Card>

            {/* Popover Menu */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        borderRadius: 2,
                        padding: '8px',
                        margin: '0px',
                        minWidth: '180px',
                        border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                    },
                }}>
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        padding: '4px',
                        minWidth: '180px',
                    }}>
                    <Stack
                        onClick={handleWhyAmISeeingThis}
                        direction="row"
                        sx={{
                            cursor: 'pointer',
                            padding: '8px 12px',
                            borderRadius: 1,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].darkGray60,
                            },
                        }}>
                        <Typography
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: 14,
                            }}>
                            Why am I seeing this?
                        </Typography>
                    </Stack>
                </Box>
            </Popover>

            {/* Modal */}
            <DialogueModal
                open={isModalOpen}
                onClose={handleModalClose}
                header="Why am I seeing this ad?"
                buttonEnabled={false}>
                <Stack spacing={2} sx={{ marginTop: 2 }}>
                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: 14,
                            lineHeight: 1.6,
                        }}>
                        Thspian aggregates advertisements from various advertisers across the platform and displays them
                        to you based on several factors including:
                    </Typography>

                    <Box component="ul" sx={{ paddingLeft: 2, margin: 0 }}>
                        <Typography
                            component="li"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: 14,
                                lineHeight: 1.6,
                                marginBottom: 1,
                            }}>
                            <strong>Location matching:</strong> Ads relevant to your geographic area
                        </Typography>
                        <Typography
                            component="li"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: 14,
                                lineHeight: 1.6,
                                marginBottom: 1,
                            }}>
                            <strong>Interest alignment:</strong> Content that matches your viewing preferences and
                            activity
                        </Typography>
                        <Typography
                            component="li"
                            sx={{
                                color: theme[themeStyle].tag_white,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                fontSize: 14,
                                lineHeight: 1.6,
                                marginBottom: 1,
                            }}>
                            <strong>Platform criteria:</strong> Internal algorithms that determine ad relevance
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            color: theme[themeStyle].tag_white,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: 14,
                            lineHeight: 1.6,
                            fontStyle: 'italic',
                        }}>
                        This helps ensure you see advertisements that are most relevant and valuable to you.
                    </Typography>
                </Stack>
            </DialogueModal>
        </>
    );
};

export { AdvertBanner };
